import React from "react";
import { Star, ShieldCheck, Check, TrendingUp, CalendarCheck } from "lucide-react";
import EstimateBar from "../components/site/EstimateBar";
import Reveal from "../components/site/Reveal";
import Typewriter from "../components/site/Typewriter";

const HERO_WORDS = [
  "louer l'esprit libre.",
  "accueillir vos voyageurs.",
  "un ménage impeccable.",
  "rentabiliser votre bien.",
  "gagner du temps.",
  "profiter de la mer.",
];
import FaqSection from "../components/site/FaqSection";
import { FoundersBand } from "../components/site/FoundersBand";
import { HomeSeo } from "../components/site/HomeSeo";
import { HowItWorks, Services, StatsBand, Zones, Platforms, Testimonials, FinalCta } from "../components/site/HomeSections";
import { IMG, PLATFORMS } from "../mock";

function Hero() {
  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-[#f3ede1] to-[#faf7f0]" data-testid="hero-section">
      <div className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full bg-[#d9895a]/15 blur-3xl" />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-14 lg:pt-20 pb-24 grid lg:grid-cols-12 gap-12 items-center relative">
        <div className="lg:col-span-6">
          <Reveal>
            <span className="inline-flex items-center gap-2 rounded-full bg-white ring-1 ring-[#e6dcc8] px-4 py-2 text-sm font-semibold text-[#1c3426] shadow-sm">
              <span className="flex">{[0, 1, 2, 3, 4].map((i) => <Star key={i} className="w-4 h-4 fill-[#d9895a] text-[#d9895a]" />)}</span>
              4,9/5 par +1 200 voyageurs en Superhôte Airbnb
            </span>
          </Reveal>
          <Reveal delay={120}>
            <h1 className="mt-7 text-[#13261c] leading-[1.05]">
              <span className="block font-logo font-black text-[52px] sm:text-[64px] lg:text-[76px] tracking-[-0.03em]">ouest<span className="text-[#d9895a]">.</span></span>
              <span className="block font-display font-medium text-[34px] sm:text-5xl lg:text-[54px] mt-1">La conciergerie pour</span>
              <span className="block font-display font-bold text-[34px] sm:text-5xl lg:text-[54px] text-[#c4703f] min-h-[2.2em] sm:min-h-[1.15em]">
                <Typewriter words={HERO_WORDS} />
              </span>
            </h1>
          </Reveal>
          <Reveal delay={180}>
            <p className="mt-5 text-lg lg:text-xl text-[#4a5249] leading-relaxed max-w-xl">
              <strong className="text-[#13261c]">Louez votre logement l'esprit libre !</strong> Annonce, voyageurs, ménage, linge : on s'occupe de tout de Saint-Malo à Dinard, vous profitez.
            </p>
          </Reveal>
          <Reveal delay={240} className="mt-9">
            <EstimateBar />
          </Reveal>
          <Reveal delay={320}>
            <ul className="mt-7 flex flex-wrap gap-x-6 gap-y-2 text-[15px] font-semibold text-[#2c3a31]">
              {["Sans frais d'entrée", "Équipe locale 7j/7", "Payé 24h après l'arrivée"].map((t) => (
                <li key={t} className="flex items-center gap-2"><span className="w-5 h-5 rounded-full bg-[#13261c] grid place-items-center"><Check className="w-3 h-3 text-white" strokeWidth={3} /></span>{t}</li>
              ))}
            </ul>
          </Reveal>
        </div>
        <div className="lg:col-span-6 relative">
          <Reveal delay={150} className="relative">
            <div className="relative h-[520px] sm:h-[600px] lg:h-[640px]" data-testid="hero-visual">
              <svg viewBox="0 0 200 200" className="absolute -top-6 right-0 w-[78%] text-[#d9895a]/25" aria-hidden="true">
                <path fill="currentColor" d="M45.3,-58.6C57.6,-47.1,65.5,-31.5,69.4,-14.6C73.3,2.3,73.2,20.5,65,34.7C56.8,48.9,40.5,59.1,22.9,65.5C5.3,71.9,-13.6,74.5,-30.4,68.6C-47.2,62.7,-61.9,48.3,-69.3,30.9C-76.7,13.5,-76.8,-6.9,-69.5,-23.4C-62.2,-39.9,-47.5,-52.5,-32,-63C-16.5,-73.5,-0.2,-81.9,14.7,-79.6C29.6,-77.3,33,-70.1,45.3,-58.6Z" transform="translate(100 100)" />
              </svg>
              <div className="absolute right-0 top-0 w-[74%] h-[88%] rounded-t-[999px] rounded-b-[36px] overflow-hidden shadow-[0_40px_80px_-30px_rgba(19,38,28,0.5)] ring-8 ring-[#faf7f0]">
                <img fetchPriority="high" src="/photos/remparts-saint-malo.jpg" alt="Les remparts de Saint-Malo Intra-Muros" className="w-full h-full object-cover object-[62%_center]" />
              </div>
              <div className="absolute left-0 bottom-0 w-[46%] aspect-square rounded-full overflow-hidden ring-8 ring-[#faf7f0] shadow-[0_30px_60px_-25px_rgba(19,38,28,0.55)]">
                <img src={IMG.dining} alt="Séjour et cuisine d'un logement accompagné par Ouest Conciergerie" className="w-full h-full object-cover object-[45%_center]" />
              </div>
              <p className="absolute right-6 bottom-2 font-script text-[#c4703f] text-3xl -rotate-6" aria-hidden="true">Saint-Malo</p>
              <svg viewBox="0 0 120 60" className="hidden sm:block absolute left-[40%] bottom-[30%] w-24 text-[#13261c]/30" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <path d="M4 40c18-26 46-34 70-20 10 6 18 6 28-2" /><path d="m96 10 8 8-10 4" />
              </svg>
            </div>
            <div className="floaty absolute left-0 top-10 bg-white rounded-3xl p-4 pr-6 shadow-[0_25px_50px_-20px_rgba(19,38,28,0.45)] flex items-center gap-3">
              <span className="w-11 h-11 rounded-2xl bg-[#f1ebdd] grid place-items-center"><TrendingUp className="w-5 h-5 text-[#c4703f]" /></span>
              <div><p className="text-xs font-semibold text-[#6d7268]">Revenus août</p><p className="text-xl font-extrabold text-[#13261c]">+ 3 480 €</p></div>
            </div>
            <div className="floaty absolute right-0 bottom-[22%] bg-white rounded-3xl p-4 shadow-[0_25px_50px_-20px_rgba(19,38,28,0.45)] w-56" style={{ animationDelay: "1.5s" }}>
              <div className="flex items-center gap-2"><CalendarCheck className="w-5 h-5 text-[#c4703f]" /><p className="text-sm font-bold text-[#13261c]">Nouvelle réservation</p></div>
              <p className="text-xs text-[#6d7268] mt-1">Famille Martin · 7 nuits · Dinard</p>
              <div className="mt-3 h-2 rounded-full bg-[#f1ebdd] overflow-hidden"><div className="h-full w-[92%] bg-[#13261c] rounded-full" /></div>
              <p className="text-[11px] mt-1.5 font-semibold text-[#c4703f]">92 % d'occupation cet été</p>
            </div>
          </Reveal>
        </div>
      </div>
      <div className="border-y border-[#e6dcc8] bg-white py-7 overflow-hidden" data-testid="platforms-marquee">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 flex items-center gap-8">
          <p className="hidden md:flex items-center gap-2 text-sm font-bold text-[#2c3a31] shrink-0"><ShieldCheck className="w-5 h-5 text-[#c4703f]" />Votre annonce diffusée sur</p>
          <div className="relative overflow-hidden flex-1 [mask-image:linear-gradient(90deg,transparent,#000_10%,#000_90%,transparent)]">
            <div className="marquee flex items-center gap-20 w-max">
              {[...PLATFORMS, ...PLATFORMS, ...PLATFORMS, ...PLATFORMS].map((p, i) => (
                <img key={i} src={p.logo} alt={p.name} className={`${p.h} w-auto object-contain`} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <HowItWorks />
      <Services />
      <StatsBand />
      <Platforms />
      <Zones />
      <HomeSeo />
      <FoundersBand />
      <Testimonials />
      <FaqSection />
      <FinalCta />
    </div>
  );
}

